'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

type ServiceItem = { title: string; desc: string };

type Props = {
  base: string;
  locale: string;
  labels: {
    services: string;
    portfolio: string;
    about: string;
    blog: string;
    reserve: string;
    seeAll: string;
    home: string;
  };
  serviceItems: ServiceItem[];
};

const NAV_ITEMS = [
  { key: 'home' as const, emoji: '🏠', path: '' },
  { key: 'portfolio' as const, emoji: '✨', path: '/portfolio' },
  { key: 'about' as const, emoji: '💜', path: '/a-propos' },
  { key: 'blog' as const, emoji: '📝', path: '/blog' },
];

const SERVICE_ITEMS = [
  { emoji: '🎬', path: '/services/ugc', index: 0 },
  { emoji: '📱', path: '/services/gestion-reseaux', index: 1 },
  { emoji: '✨', path: '/services/coaching', index: 2 },
  { emoji: '🎨', path: '/services/creative-strategy', index: 3 },
];

export default function MobileMenuClient({ base, locale, labels, serviceItems }: Props) {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setServicesOpen(false);
  };

  function switchLocale(newLocale: string) {
    const path = window.location.pathname;
    window.location.href = path.replace(/^\/(fr|en)/, `/${newLocale}`);
  }

  const overlay = (
    <>
      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          background: 'rgba(10,4,20,0.55)',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Bottom sheet */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
          fontFamily: "var(--font-poppins), 'Poppins', sans-serif",
          transform: open ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.35s cubic-bezier(.4,0,.2,1)',
          overflowY: 'auto',
          maxHeight: '92vh',
        }}
      >
        {/* Gradient décoratif */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '120px',
            background: 'linear-gradient(160deg, #fce4f7 0%, #f3e8ff 50%, transparent 100%)',
            pointerEvents: 'none',
            borderRadius: '20px 20px 0 0',
          }}
        />

        {/* Handle */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '10px',
            paddingBottom: '4px',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '4px',
              borderRadius: '999px',
              background: 'rgba(26,10,30,0.12)',
            }}
          />
        </div>

        {/* Header : logo + close */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 20px 8px',
          }}
        >
          <a
            href={base}
            onClick={close}
            style={{
              fontFamily: "'Fraunces', serif",
              fontStyle: 'italic',
              fontWeight: 700,
              fontSize: '1.1rem',
              color: '#1a0a1e',
              textDecoration: 'none',
              letterSpacing: '-0.02em',
            }}
          >
            cindy <span style={{ fontStyle: 'normal' }}>✨</span>
          </a>
          <button
            onClick={close}
            aria-label="Fermer le menu"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(26,10,30,0.06)',
              border: 'none',
              cursor: 'pointer',
              color: '#1a0a1e',
              fontSize: '1rem',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Liens de navigation */}
        <nav
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '2px 0 0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Accueil */}
          <a href={base} onClick={close} style={navLinkStyle}>
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: '#fe7aa8',
                letterSpacing: '0.1em',
                minWidth: '22px',
              }}
            >
              01
            </span>
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#1a0a1e',
                flex: 1,
              }}
            >
              {labels.home}
            </span>
            <span style={{ fontSize: '0.9rem', opacity: 0.25 }}>→</span>
          </a>

          {/* Services — accordion */}
          <div>
            <button
              onClick={() => setServicesOpen((v) => !v)}
              style={
                {
                  ...navLinkStyle,
                  background: servicesOpen ? 'rgba(254,122,168,0.06)' : 'transparent',
                  width: '100%',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                } as React.CSSProperties
              }
            >
              <span
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: '#fe7aa8',
                  letterSpacing: '0.1em',
                  minWidth: '22px',
                }}
              >
                02
              </span>
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: 'italic',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#1a0a1e',
                  flex: 1,
                }}
              >
                {labels.services}
              </span>
              <span
                style={{
                  fontSize: '0.8rem',
                  color: '#fe7aa8',
                  transition: 'transform 0.22s',
                  display: 'inline-block',
                  transform: servicesOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                ▾
              </span>
            </button>

            <div
              style={{
                maxHeight: servicesOpen ? '320px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.35s cubic-bezier(.4,0,.2,1)',
              }}
            >
              <div
                style={{
                  padding: '4px 14px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                }}
              >
                {SERVICE_ITEMS.map((s) => {
                  const title = serviceItems[s.index]?.title;
                  const href = 'href' in s ? (s.href as string) : `${base}${s.path}`;
                  const isExternal = 'href' in s;
                  return (
                    <a
                      key={s.index}
                      href={href}
                      target={isExternal ? '_blank' : undefined}
                      rel={isExternal ? 'noopener noreferrer' : undefined}
                      onClick={!isExternal ? close : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '9px 13px',
                        borderRadius: '11px',
                        background: 'linear-gradient(135deg, #fce4f7 0%, #f3e8ff 100%)',
                        textDecoration: 'none',
                        border: '1px solid rgba(254,122,168,0.15)',
                      }}
                    >
                      <span style={{ fontSize: '1rem', flexShrink: 0 }}>{s.emoji}</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a0a1e' }}>
                        {title}
                      </span>
                      <span
                        style={{
                          marginLeft: 'auto',
                          fontSize: '0.75rem',
                          color: '#c44fb5',
                          opacity: 0.6,
                        }}
                      >
                        →
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <a href={`${base}/portfolio`} onClick={close} style={navLinkStyle}>
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: '#fe7aa8',
                letterSpacing: '0.1em',
                minWidth: '22px',
              }}
            >
              03
            </span>
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#1a0a1e',
                flex: 1,
              }}
            >
              {labels.portfolio}
            </span>
            <span style={{ fontSize: '0.9rem', opacity: 0.25 }}>→</span>
          </a>

          <a href={`${base}/a-propos`} onClick={close} style={navLinkStyle}>
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: '#fe7aa8',
                letterSpacing: '0.1em',
                minWidth: '22px',
              }}
            >
              04
            </span>
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#1a0a1e',
                flex: 1,
              }}
            >
              {labels.about}
            </span>
            <span style={{ fontSize: '0.9rem', opacity: 0.25 }}>→</span>
          </a>

          <a href={`${base}/blog`} onClick={close} style={navLinkStyle}>
            <span
              style={{
                fontSize: '0.65rem',
                fontWeight: 700,
                color: '#fe7aa8',
                letterSpacing: '0.1em',
                minWidth: '22px',
              }}
            >
              05
            </span>
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#1a0a1e',
                flex: 1,
              }}
            >
              {labels.blog}
            </span>
            <span style={{ fontSize: '0.9rem', opacity: 0.25 }}>→</span>
          </a>
        </nav>

        {/* Footer : langue + CTA */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '10px 18px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <div
            style={{
              height: '1px',
              background: 'linear-gradient(90deg, #fe7aa8 0%, #c44fb5 50%, transparent 100%)',
              opacity: 0.2,
              marginBottom: '2px',
            }}
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            {(['fr', 'en'] as const).map((l) => (
              <button
                key={l}
                onClick={() => switchLocale(l)}
                style={{
                  padding: '6px 16px',
                  borderRadius: '999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background:
                    l === locale ? 'linear-gradient(135deg, #fe7aa8, #c44fb5)' : 'transparent',
                  border: l === locale ? 'none' : '1.5px solid rgba(26,10,30,0.15)',
                  color: l === locale ? '#fff' : 'rgba(26,10,30,0.45)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: l === locale ? '0 2px 10px rgba(254,122,168,0.35)' : 'none',
                }}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <a
            href={`${base}/contact`}
            onClick={close}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '13px 20px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #fe7aa8 0%, #f43f8b 100%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.95rem',
              textDecoration: 'none',
              boxShadow: '0 4px 18px rgba(254,122,168,0.4)',
              letterSpacing: '-0.01em',
            }}
          >
            {labels.reserve}
          </a>
        </div>
      </div>
    </>
  );

  return (
    <>
      <button
        className="t-nav-burger"
        onClick={() => (open ? close() : setOpen(true))}
        aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={open}
        style={{ position: 'relative', zIndex: 10000 }}
      >
        <span className={`t-burger-bar${open ? ' t-burger-open-1' : ''}`} />
        <span className={`t-burger-bar${open ? ' t-burger-open-2' : ''}`} />
        <span className={`t-burger-bar${open ? ' t-burger-open-3' : ''}`} />
      </button>

      {mounted && createPortal(overlay, document.body)}
    </>
  );
}

const navLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  padding: '10px 20px',
  textDecoration: 'none',
  borderBottom: '1px solid rgba(26,10,30,0.05)',
  transition: 'background 0.15s',
  WebkitTapHighlightColor: 'transparent',
};

function hoverOn(e: React.MouseEvent) {
  (e.currentTarget as HTMLElement).style.background = 'rgba(254,122,168,0.04)';
}
function hoverOff(e: React.MouseEvent) {
  (e.currentTarget as HTMLElement).style.background = 'transparent';
}
